// MCResults.tsx — Live bar chart for multiple choice results

import React from 'react';
import { motion } from 'framer-motion';
import type { MCConfig } from '@types-local/quiz.types';
import type { MCResults as MCResultsType } from '@types-local/room.types';

const OPTION_COLORS = ['bg-brand-500', 'bg-orange-500', 'bg-green-500', 'bg-purple-500', 'bg-blue-500', 'bg-pink-500'];
const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];

interface MCResultsProps {
  config: MCConfig;
  results: MCResultsType;
}

export default function MCResults({ config, results }: MCResultsProps): React.ReactElement {
  const maxCount = Math.max(...results.counts, 1);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600">{results.totalResponses} responses</p>
      </div>
      <div className="space-y-2.5">
        {config.options.map((opt, i) => {
          const pct = Math.round((results.counts[i] / maxCount) * 100);
          const isCorrect = opt.isCorrect;
          return (
            <div key={opt.id}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className={`font-semibold ${isCorrect ? 'text-green-400' : 'text-slate-700'}`}>
                  {OPTION_LABELS[i]}. {opt.text} {isCorrect && '✓'}
                </span>
                <span className="text-slate-500">{results.counts[i]}</span>
              </div>
              <div className="h-3 bg-surface-elevated rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${isCorrect ? 'bg-green-500' : OPTION_COLORS[i]}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, type: 'spring' }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
